"use client";

import { useRef, useMemo, useEffect, useState, useCallback } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { RoundedBox } from "@react-three/drei";
import { BufferGeometry, Color, InstancedMesh, Mesh, Object3D, Vector3 } from "three";
import { CUBE_MATERIAL_PROPS } from "./cubeConstants";

// ── Colors ─────────────────────────────────────────────────────────────
const VIOLET = new Color("#8635F6");
const VIOLET_LIGHT = new Color("#c084fc");
const tempObj = new Object3D();
const tempColor = new Color();

// ── Geometry: EXACT CubeIcon cube (0.25, radius 0.04, smoothness 3) ────
const SIZE: [number, number, number] = [0.25, 0.25, 0.25];
const RADIUS = 0.04;
const SMOOTHNESS = 3;

// ── Mouse ref (exported for CTA.tsx) ───────────────────────────────────
export const ctaMouseRef: { current: { x: number; y: number } | null } = {
  current: null,
};

// ── Invisible RoundedBox for geometry capture (CubeIcon pattern) ───────
function Geo({ onReady }: { onReady: (g: BufferGeometry) => void }) {
  const ref = useRef<Mesh>(null);
  useEffect(() => {
    if (ref.current) onReady(ref.current.geometry);
  }, [onReady]);
  return (
    <RoundedBox
      ref={ref}
      args={SIZE}
      radius={RADIUS}
      smoothness={SMOOTHNESS}
      visible={false}
    >
      <meshBasicMaterial />
    </RoundedBox>
  );
}

// ── Grid of cubes with hover wave ──────────────────────────────────────
function Grid({
  invalidateFnRef,
}: {
  invalidateFnRef: React.MutableRefObject<(() => void) | null>;
}) {
  const meshRef = useRef<InstancedMesh>(null);
  const [geo, setGeo] = useState<BufferGeometry | null>(null);
  const { camera, invalidate } = useThree();
  const hoveredRef = useRef<number | null>(null);

  const onGeoReady = useCallback(
    (g: BufferGeometry) => setGeo(g.clone()),
    []
  );

  // Grid: dense mosaic of small cubes filling entire CTA card
  // Camera z=18, fov=35: visible height ≈ 11.3, width ≈ 34 (at ~3:1 aspect)
  // 0.25 spacing — dense grid to fill full-width card
  const spacing = 0.25; // CUBE_GAP — same as CubeIcon
  const cols = 300;
  const rows = 105;

  const positions = useMemo(() => {
    const pos: [number, number, number][] = [];
    for (let c = 0; c < cols; c++) {
      for (let r = 0; r < rows; r++) {
        pos.push([
          (c - (cols - 1) / 2) * spacing,
          (r - (rows - 1) / 2) * spacing,
          0,
        ]);
      }
    }
    return pos;
  }, []);

  // Hover wave state (from BackgroundCubes)
  const liftTargets = useRef(new Float32Array(positions.length));
  const liftCurrent = useRef(new Float32Array(positions.length));

  // Expose invalidate
  useEffect(() => {
    invalidateFnRef.current = invalidate;
  }, [invalidate, invalidateFnRef]);

  // Init matrices + colors
  useEffect(() => {
    if (!meshRef.current || !geo) return;
    for (let i = 0; i < positions.length; i++) {
      tempObj.position.set(positions[i][0], positions[i][1], positions[i][2]);
      tempObj.scale.setScalar(1);
      tempObj.updateMatrix();
      meshRef.current.setMatrixAt(i, tempObj.matrix);
      meshRef.current.setColorAt(i, VIOLET);
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
    if (meshRef.current.instanceColor)
      meshRef.current.instanceColor.needsUpdate = true;
    setTimeout(() => invalidate(), 50);
  }, [positions, geo, invalidate]);

  // Hover wave (from BackgroundCubes useFrame)
  useFrame(() => {
    if (!meshRef.current) return;

    const mousePos = ctaMouseRef.current;
    let newHovered: number | null = null;

    if (mousePos) {
      const ndcX = mousePos.x * 2 - 1;
      const ndcY = -(mousePos.y * 2 - 1);
      const vec = new Vector3(ndcX, ndcY, 0.5);
      vec.unproject(camera);
      const dir = vec.sub(camera.position).normalize();
      const t = -camera.position.z / dir.z;
      const wx = camera.position.x + dir.x * t;
      const wy = camera.position.y + dir.y * t;

      let ci = -1;
      let cd = Infinity;
      for (let i = 0; i < positions.length; i++) {
        const dx = positions[i][0] - wx;
        const dy = positions[i][1] - wy;
        const d = dx * dx + dy * dy;
        if (d < cd) {
          cd = d;
          ci = i;
        }
      }
      if (cd < 0.15) newHovered = ci;
    }

    if (newHovered !== hoveredRef.current) {
      hoveredRef.current = newHovered;
      liftTargets.current.fill(0);
      if (newHovered !== null) {
        const [hx, hy] = positions[newHovered];
        for (let i = 0; i < positions.length; i++) {
          const dx = positions[i][0] - hx;
          const dy = positions[i][1] - hy;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (i === newHovered) {
            liftTargets.current[i] = 1.0;
          } else if (dist < 0.5) {
            liftTargets.current[i] = 0.9;
          } else if (dist < 1.25) {
            liftTargets.current[i] = 0.7;
          } else if (dist < 2.5) {
            liftTargets.current[i] = 0.5;
          } else if (dist < 3.75) {
            liftTargets.current[i] = 0.3;
          } else if (dist < 5.0) {
            liftTargets.current[i] = 0.15;
          } else if (dist < 6.6) {
            liftTargets.current[i] = 0.05;
          }
        }
      }
    }

    let needsUpdate = false;
    for (let i = 0; i < positions.length; i++) {
      const target = liftTargets.current[i];
      const cur = liftCurrent.current[i];
      const next = cur + (target - cur) * 0.12;

      if (Math.abs(next - cur) > 0.001) {
        liftCurrent.current[i] = next;
        needsUpdate = true;
      }

      const [x, y, z] = positions[i];
      tempObj.position.set(x, y, z + liftCurrent.current[i] * 0.5);
      tempObj.scale.setScalar(1);
      tempObj.updateMatrix();
      meshRef.current.setMatrixAt(i, tempObj.matrix);

      const lift = liftCurrent.current[i];
      if (lift > 0.01) {
        tempColor.copy(VIOLET).lerp(VIOLET_LIGHT, lift);
        meshRef.current.setColorAt(i, tempColor);
      } else {
        meshRef.current.setColorAt(i, VIOLET);
      }
    }

    if (needsUpdate) {
      meshRef.current.instanceMatrix.needsUpdate = true;
      if (meshRef.current.instanceColor)
        meshRef.current.instanceColor.needsUpdate = true;
      invalidate();
    }
  });

  return (
    <>
      <Geo onReady={onGeoReady} />
      {geo && (
        <instancedMesh
          ref={meshRef}
          args={[geo, undefined, positions.length]}
          frustumCulled={false}
        >
          <meshPhysicalMaterial color="#ffffff" {...CUBE_MATERIAL_PROPS} />
        </instancedMesh>
      )}
    </>
  );
}

// ── Canvas wrapper ─────────────────────────────────────────────────────
export default function CTABackgroundCubes({
  invalidateFnRef,
}: {
  invalidateFnRef: React.MutableRefObject<(() => void) | null>;
}) {
  return (
    <Canvas
      style={{
        width: "100%",
        height: "100%",
        position: "absolute",
        top: 0,
        left: 0,
      }}
      gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      dpr={[1, 2]}
      frameloop="demand"
      camera={{ position: [0, 0, 27], fov: 35, near: 0.1, far: 100 }}
      onCreated={({ gl, invalidate }) => {
        gl.domElement.addEventListener(
          "webglcontextlost",
          (e) => e.preventDefault(),
          false
        );
        setTimeout(() => invalidate(), 100);
      }}
    >
      <ambientLight intensity={1.275} />
      <directionalLight
        position={[-3, 8, 10]}
        intensity={2.55}
        color="#ffffff"
      />
      <Grid invalidateFnRef={invalidateFnRef} />
    </Canvas>
  );
}
